import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import styles from './addDaycareGroupUrlDialog.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ADD_DAYCARE_GROUP_URL } from '@/modules/API/daycareGroup/daycareGroupUrl'
import { DaycareGroup, DaycareGroupUrlTypes } from '@/types/DaycareGroup'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { getDaycareGroupsIconTextToJSX } from '@/modules/helper/iconsHelper'
import SelectUniqueIcon, {
    SelectUniqueIconOptions
} from '@/components/_custom/Selector/selectUniqueIcon/selectUniqueIcon'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddDaycareGroupUrlDialogProps {
    open: boolean
    handleClose: () => void
    group: DaycareGroup
    refetchUrl: () => void
}

const AddDaycareGroupUrlDialog: FunctionComponent<AddDaycareGroupUrlDialogProps> = ({
    open,
    handleClose,
    group,
    refetchUrl
}) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [urlText, setUrlText] = useState<string>('')
    const [urlLink, setUrlLink] = useState<string>('')
    const [urlIcon, setUrlIcon] = useState<string>(DaycareGroupUrlTypes.ExitToApp)
    const [daycareIds, setDaycareIds] = useState<Array<string>>([])

    const [addDaycareGroupURL] = useMutation(ADD_DAYCARE_GROUP_URL, {
        variables: {
            groupId: group.id,
            text: urlText,
            url: urlLink,
            icon: urlIcon,
            daycareIds: daycareIds.map((daycareId) => parseInt(daycareId))
        },
        onCompleted: () => {
            showNotificationSuccess(t('urlAdded'))

            refetchUrl()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('urlAddedError'))
        }
    })

    const daycareIdsOptions: SelectOptionsDeprecated = [
        {
            options:
                group.daycares.map((daycare) => ({
                    name: daycare.city + ' - ' + daycare.name,
                    value: daycare.id.toString()
                })) || []
        }
    ]

    const iconsOptions: SelectUniqueIconOptions[] = Object.values(DaycareGroupUrlTypes).map((icon) => ({
        title: icon,
        icon: getDaycareGroupsIconTextToJSX(icon, styles.SelectedIconSize)
    }))

    return (
        <Dialog open={open} handleClose={handleClose} maxWidth={'md'}>
            <DialogHeader title={t('addUrl')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={urlText}
                        setValue={setUrlText}
                        required
                    />
                    <UserInput
                        label={t('url')}
                        className={styles.UserInput}
                        value={urlLink}
                        setValue={setUrlLink}
                        required
                    />
                    <SelectUniqueIcon
                        icon={getDaycareGroupsIconTextToJSX(urlIcon as DaycareGroupUrlTypes, styles.SelectedIconSize)}
                        title={t('icon')}
                        setIcon={setUrlIcon}
                        iconsOptions={iconsOptions}
                        className={styles.UserInput}
                    />
                    <SelectMultiple
                        selectOptions={daycareIdsOptions}
                        selectValues={daycareIds}
                        setSelectValues={setDaycareIds}
                        className={styles.UserInput}
                        label={t('myDaycares')}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        onClick={addDaycareGroupURL}
                        disabled={!urlText || !urlLink || daycareIds.length === 0}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddDaycareGroupUrlDialog
